import {
  StaffFormData,
  StaffUpdateFormData,
} from "@/components/staff/StaffFormDialog";
import { api } from "@/lib/api";
import {
  Customer,
  CustomerWithDetails,
  UserInput,
  UserOutput,
  StaffApiResponse,
  CustomerFullCreate,
  CustomerFullInput,
  CustomerUpdate,
} from "@/types";
import {
  CustomerQueryParams,
  CustomerApiResponse,
} from "@/types/customer.api.types";

export interface CustomerLookupOption {
  id: string;
  name: string;
  phone: string | null;
}

export const userService = {
  /**
   * Type-ahead customer lookup for filter pickers.
   *
   * Deliberately does NOT carry the admin role guard that getAllCustomers has:
   * /users/lookup is staff-accessible by design, and the pages using it are
   * open to staff. Returns id/name/phone only.
   */
  async lookupCustomers(search?: string): Promise<CustomerLookupOption[]> {
    const response = await api.get<{
      status: string;
      message: string;
      data: CustomerLookupOption[];
    }>("/users/lookup", { params: search ? { search } : {} });

    return Array.isArray(response.data?.data) ? response.data.data : [];
  },

  async getAllCustomers(): Promise<CustomerWithDetails[]> {
    // Check if user is admin before allowing access
    if (typeof window !== 'undefined') {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const userRole = user.role?.toUpperCase();
          // Only allow ADMIN or manager roles
          if (userRole !== 'ADMIN' && userRole !== 'MANAGER') {
            throw new Error('Access denied: Admin privileges required to fetch all customers');
          }
        } catch (error) {
          throw new Error('Access denied: Unable to verify user role');
        }
      } else {
        throw new Error('Access denied: User not authenticated');
      }
    }
    const limit = 100;
    let page = 1;
    let totalPages = 1;
    const allCustomers: CustomerWithDetails[] = [];

    do {
      const response = await api.get(
        `/users/allcustomers?page=${page}&limit=${limit}&sortBy=createdAt&sortOrder=desc`
      );

      const pageCustomers: CustomerWithDetails[] = Array.isArray(
        response.data?.data
      )
        ? response.data.data
        : [];

      allCustomers.push(...pageCustomers);
      totalPages = response.data?.pagination?.totalPages || 1;
      page += 1;
    } while (page <= totalPages);

    // Deduplicate by customer id in case backend pages overlap.
    return Array.from(new Map(allCustomers.map((c) => [c.id, c])).values());
  },

  async getAllStaffs(): Promise<StaffApiResponse[]> {
    // Check if user is admin before allowing access
    if (typeof window !== 'undefined') {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const userRole = user.role?.toUpperCase();
          // Only allow ADMIN or manager roles
          if (userRole !== 'ADMIN' && userRole !== 'MANAGER') {
            throw new Error('Access denied: Admin privileges required to fetch all staff');
          }
        } catch (error) {
          throw new Error('Access denied: Unable to verify user role');
        }
      } else {
        throw new Error('Access denied: User not authenticated');
      }
    }
    const response = await api.get("/users/allstaffs");
    return response.data.data;
  },

  async getCustomer(id: string): Promise<Customer> {
    const response = await api.get(`/customers/${id}`);
    return response.data.data;
  },

  async createUser(user: UserInput): Promise<UserOutput> {
    const response = await api.post("/users/create", user);
    return response.data.data;
  },

  async createFullCustomer(customer: CustomerFullInput): Promise<Customer> {
    const response = await api.post("/customerFullCreate", customer);
    return response.data.data;
  },

  async updateCustomer(
    id: string,
    customer: CustomerUpdate
  ): Promise<Customer> {
    const response = await api.put(`/users/${id}`, customer);
    return response.data.data;
  },

  async staffUpdate(
    id: string,
    staff: Partial<StaffUpdateFormData>
  ): Promise<UserOutput> {
    const response = await api.put(`/users/${id}`, staff);
    return response.data.data;
  },

  async deleteCustomer(id: string): Promise<void> {
    await api.delete(`/users/${id}`);
  },

  async getCustomersPaginated(
    params: CustomerQueryParams
  ): Promise<CustomerApiResponse> {
    // Check if user is admin before allowing access
    if (typeof window !== "undefined") {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const userRole = user.role?.toUpperCase();
          // Only allow ADMIN or manager roles
          if (userRole !== "ADMIN" && userRole !== "MANAGER") {
            throw new Error(
              "Access denied: Admin privileges required to fetch all customers"
            );
          }
        } catch (error) {
          throw new Error("Access denied: Unable to verify user role");
        }
      } else {
        throw new Error("Access denied: User not authenticated");
      }
    }

    // Build query string from params, filtering out undefined/null/empty values
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, String(value));
      }
    });

    const queryString = queryParams.toString();
    const url = queryString
      ? `/users/allcustomers?${queryString}`
      : "/users/allcustomers";

    const response = await api.get(url);
    return response.data;
  },
};
